import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ILocalizationProps,
  IInitProps,
  ReactINTLContextType,
  translationMapKeysType,
  IGetTranlationsJSON,
} from 'utils/types';
import noop from 'utils/noop';
import useForceUpdate from 'hooks/useForceUpdate';
import {
  DEFAULT_LOCALE, baseUrl,
  LANGUAGE_COOKIE_NAME,
  CURRENT_LANGUAGE_COOKIE_NAME,
  SECONARY_PATHS_FOR_LANGUAGE_COOKIE_NAME,
} from 'configs/constants';
import { deleteCookie, getCookie, setCookie } from 'utils/cookie';
import IS_BROWSER from 'utils/isBrowser';
import { ILanguagesCodes, languages } from 'data/languages';
import Logger from 'services/logger';
import createFetch from 'utils/createFetch';
import useMount from 'hooks/useMount';

let initialized = false;
const fetch = createFetch();

const getDefaultLanguageFromBrowser = () => {
  const browserLanguage = IS_BROWSER && navigator.language;
  if (browserLanguage) {
    const lowerCaseBrowserLanguage = browserLanguage.toLocaleLowerCase();
    if (lowerCaseBrowserLanguage === 'zh-hant' || lowerCaseBrowserLanguage === 'zh_hant') {
      // TODO it's temparary solution, we will remove this after mapping this part in backend
      return 'zh_HANT';
    }
    const languagesData = Object.keys(languages);
    const lang = (browserLanguage.includes('-') ? browserLanguage.slice(0, browserLanguage.indexOf('-')) : browserLanguage).toLowerCase();
    if (languagesData.includes(lang)) {
      return lang;
    }
  }

  return DEFAULT_LOCALE;
};

const init = async ({
  force = false,
  multipleRequests = false,
  localesUrl,
  withConsole,
  requestHeader,
}: IInitProps): Promise<any> => {
  if (!force && initialized) {
    return;
  }

  try {
    const messages = await fetch(localesUrl, { withoutLanguageHeader: false, headers: requestHeader });

    if (!multipleRequests) {
      initialized = true;
    }

    return messages;
  } catch (err) {
    Logger.error(err as string);
  }
};

const ReactINTLContext = createContext({} as ReactINTLContextType);

const translations = new Map<string, translationMapKeysType>();

function ReactINTLProvider({
  children = null,
  projectName = '',
  force = false,
  language,
  defaultTranslatedData,
  mergeDefaultTranslatedData,
  withConsole,
  requestHeader,
  ...props
}: ILocalizationProps): ReactElement {
  const forceUpdate = useForceUpdate();
  const [isTranslationsFetched, setIsTranslationsFetched] = useState(false);

  const getLocale = useCallback((key: any) => getCookie(key), []);

  const setLocale = useCallback((key: any, value: any) => {
    setIsTranslationsFetched(false);
    setCookie(key, value);
    forceUpdate();
  }, [forceUpdate]);

  useMount(() => {
    deleteCookie(LANGUAGE_COOKIE_NAME, SECONARY_PATHS_FOR_LANGUAGE_COOKIE_NAME);
    deleteCookie(CURRENT_LANGUAGE_COOKIE_NAME, SECONARY_PATHS_FOR_LANGUAGE_COOKIE_NAME);
  });

  const languageFromStorage = getLocale(LANGUAGE_COOKIE_NAME) as ILanguagesCodes;
  const browserLanguage = useMemo(getDefaultLanguageFromBrowser, []);

  const currentLanguage: ILanguagesCodes = (language || languageFromStorage || browserLanguage) as ILanguagesCodes;

  const loadTranslations = useCallback((withConsole?: boolean) => init({
    force,
    multipleRequests: true,
    localesUrl: `${baseUrl}/localizations/${currentLanguage}/messages?project=reusable_components${projectName ? `,${projectName}` : ''}`,
    withConsole,
    requestHeader,
  }), [force, currentLanguage, projectName, requestHeader]);

  if (defaultTranslatedData && !mergeDefaultTranslatedData) {
    translations.set(currentLanguage, defaultTranslatedData);
  }

  useEffect(() => {
    (async () => {
      setLocale(CURRENT_LANGUAGE_COOKIE_NAME, currentLanguage);
      const messages = translations.get(currentLanguage) || await loadTranslations(withConsole);

      const translationsData = defaultTranslatedData && mergeDefaultTranslatedData
        ? { ...messages, ...defaultTranslatedData }
        : messages;

      translations.set(currentLanguage, translationsData);
      setLocale(CURRENT_LANGUAGE_COOKIE_NAME, currentLanguage);
      setIsTranslationsFetched(true);
      forceUpdate();
    })();
  }, [forceUpdate, currentLanguage, loadTranslations, setLocale, defaultTranslatedData, mergeDefaultTranslatedData, withConsole]);

  const translate = useCallback(() => translations.get(currentLanguage) as translationMapKeysType, [currentLanguage]);

  const reactINTLContextProviderValue = useMemo(() => ({
    translate,
    browserLanguage,
    getLocale,
    setLocale,
    currentLanguage,
    projectName,
    isTranslationsFetched,
    ...props,
  }), [browserLanguage, currentLanguage, getLocale, isTranslationsFetched, projectName, props, setLocale, translate]);

  return (
    <ReactINTLContext.Provider
      value={reactINTLContextProviderValue}
    >
      {children}
    </ReactINTLContext.Provider>
  );
}

const useTranslator = () => {
  const { translate: tr = noop }: ReactINTLContextType = useContext(ReactINTLContext);
  const translatedObj = tr();

  const translate = useCallback((str: string, values?: { [key: string]: any }) => {
    const translatedLocale = translatedObj && translatedObj[str] ? translatedObj[str] : str;
    const dynamics = Object.entries(values || {});

    if (!dynamics.length) return translatedLocale;
    return dynamics.reduce((result: string, [key, value]) => {
      const re = new RegExp(`{{${key}}}`, 'g');
      // eslint-disable-next-line
      const finalResult = result.replace('href={`${basePath}terms-of-use`}', 'href="{{basePath}}privacy-policy\"').replace(re, value);
      return finalResult;
    }, translatedLocale);
  }, [translatedObj]);

  return translate;
};

const useIsTranslationsFetched = () => {
  const { isTranslationsFetched }: ReactINTLContextType = useContext(ReactINTLContext);

  return isTranslationsFetched;
};

const useLanguageByUser = () => {
  const {
    browserLanguage,
    getLocale,
    setLocale,
    currentLanguage,
    projectName,
    ...props
  }: ReactINTLContextType = useContext(ReactINTLContext);

  return {
    browserLanguage,
    getLocale,
    setLocale,
    currentLanguage,
    projectName,
    ...props,
  };
};

const getTranlationsJSON = ({
  language,
  projectName,
  withConsole,
  requestHeader,
}: IGetTranlationsJSON) => init({
  force: false,
  multipleRequests: true,
  localesUrl: `${baseUrl}/localizations/${language}/messages?project=reusable_components${projectName ? `,${projectName}` : ''}`,
  withConsole,
  requestHeader,
});

export {
  ReactINTLProvider,
  init,
  useTranslator,
  getDefaultLanguageFromBrowser,
  useLanguageByUser,
  getTranlationsJSON,
  useIsTranslationsFetched,
};
